import Utils from './utils.js';
import Connector, { PARAM_TYPE, PARAM_UNITS } from './connector.js';
import CompoundParameter from './compoundParameter.js';
import Spline from './spline.js';

/**
 * A class that represents an input connector of a node.
 * */
export default class InputConnector extends Connector
{
	constructor(eType, strName, pParentNode, initialValue = 1, { minValue = undefined, maxValue = undefined, dropdownOptions = undefined, 
												isConnectorHidden = false, isWholeInputHidden = false, units = PARAM_UNITS.NONE })
	{
		super(eType, strName, pParentNode);

		this.staticValue = initialValue;
		this.lastComputedValue = this.staticValue;
		this.expression = this.getExpression();
		this.useExpression = false;

		// If true, the connector for this input is hidden in the node editor
		this.isConnectorHidden = isConnectorHidden;
		// If true, the entire input entry for this input is hidden in the node editor
		this.isWholeNodeInputHidden = isWholeInputHidden;
		// If true, instructs clients that this parameter should not visible in 
		// any user-facing UI that is not an editor.
		this.isInternal = false;

		this.connectedNodeId = -1;
		this.connectedNodeOutputName = null;

		this.minValue = 0;
		this.maxValue = (eType === PARAM_TYPE.FLOAT) ? 3 : 25;
        this.tags = [];

		this.specId = '';

		this.units = units;

		this.dropdownOptions = [];

		if(Utils.isDropdownParam(eType))
		{
			if(dropdownOptions)
				this.dropdownOptions = dropdownOptions;
		}
		else
		{
			if(minValue !== undefined)
				this.minValue = minValue;

			if(maxValue !== undefined)
				this.maxValue = maxValue;
		}
	}

	getConfigDict()
	{
		return { 
			minValue: this.minValue, 
			maxValue: this.maxValue, 
			dropdownOptions: this.dropdownOptions.slice(), 
			isConnectorHidden: this.isConnectorHidden,
			isWholeInputHidden: this.isWholeNodeInputHidden, 
			isInternal: this.isInternal,
			units: this.units
		};
	}

	setFromConfigDict(pInfo)
	{
		this.minValue = pInfo['minValue'];
		this.maxValue = pInfo['maxValue'];
		this.dropdownOptions = pInfo['dropdownOptions'];
		this.isConnectorHidden = pInfo['isConnectorHidden'];
		this.isWholeNodeInputHidden = pInfo['isWholeInputHidden'];
		this.isInternal = pInfo['isInternal'];
		this.units = pInfo['units'];
	}

	getUnits()
	{
		return this.units;
	}

	setUnits(eType)
	{
		this.units = Number(eType);
	}

	getDropdownOptions()
	{
		return this.dropdownOptions;
	}

	addDropdownOption(strKey, strLabel)
	{
		this.dropdownOptions.push({ key: strKey, label: strLabel });
	}

	deleteDropdownOption(strKey)
	{
		for(let idx = this.dropdownOptions.length - 1; idx >= 0; idx--)
		{
			if(this.dropdownOptions[idx].key === strKey)
				this.dropdownOptions.splice(idx, 1);
		}
	}

	renameDropdownKey(strKey, strNewKey)
	{
		for(let idx = this.dropdownOptions.length - 1; idx >= 0; idx--)
		{
			if(this.dropdownOptions[idx].key === strKey)
				this.dropdownOptions[idx].key = strNewKey;
		}
	}

	renameDropdownLabel(strKey, strNewLabel)
	{
		for(let idx = this.dropdownOptions.length - 1; idx >= 0; idx--)
		{
			if(this.dropdownOptions[idx].key === strKey)
				this.dropdownOptions[idx].label = strNewLabel;
		}
	}

	setDropdownOptions(pDropdownChoices)
	{
		this.dropdownOptions = pDropdownChoices;
	}

	setUseExpression(bValue)
	{
		if(this.useExpression === bValue)
			return;

		this.useExpression = bValue;

		// We overwrite the expression with the latest value,
		// otherwise we ignore the edits
		if(bValue)
			this.setExpression(this.getLastResult());

		this.onInputChanged();
	}

	getUseExpression()
	{
		return this.useExpression;
	}

	setExpression(strValue)
	{
		if(strValue instanceof Object)
			strValue = JSON.stringify(strValue);

		this.expression = strValue;
		this.onInputChanged();
	}

	getExpression()
	{
		let pExpr = this.expression;

		if(pExpr instanceof Object)
			pExpr = JSON.stringify(pExpr);

		return pExpr;
	}

	disconnect()
	{
		let pSubnet = this.getOwnNode().getParent();
		let strOutputNameCopy = this.connectedNodeOutputName;
		let idNodeIdCopy = this.connectedNodeId;

		this.connectedNodeId = -1;
		this.connectedNodeOutputName = null;

		// Find the node connected to us, if any, and disconnect:
		let pOtherNode = pSubnet.getNode(idNodeIdCopy);
		if(pOtherNode)
		{
			let pOutput = pOtherNode.getOutput(strOutputNameCopy);
			if(pOutput)
				pOutput.disconnect(this.parentNode.getId(), this.name);
		}

		this.onInputChanged();
	}

	saveStaticValue()
	{
		if(this.staticValue && this.staticValue.saveToObject)
			return this.staticValue.saveToObject();
		else
			return this.staticValue;
	}

	loadStaticValueFrom(pObj)
	{
		if(pObj && pObj['type'] === 'compound')
			this.setStaticValue(CompoundParameter.loadFromObject(pObj));
		else
			this.setStaticValue(pObj);
	}

	saveToObject()
	{
		let pRes = super.saveToObject();

		pRes['sv'] = this.saveStaticValue();

		if(this.connectedNodeId != -1)
			pRes['cni'] = this.connectedNodeId;
		if(this.connectedNodeOutputName)
			pRes['cno'] = this.connectedNodeOutputName;

		const strExpression = this.getExpression();
		if(strExpression && strExpression.length > 0)
			pRes['e'] = strExpression;

		if(this.useExpression)
			pRes['usE'] = this.useExpression;

		if(this.units !== PARAM_UNITS.NONE)
			pRes['un'] = this.units;

		if(this.isInternal)
			pRes['isInternal'] = this.isInternal;

		pRes['mi'] = this.minValue;
		pRes['ma'] = this.maxValue;
		if(this.tags.length > 0)
			pRes['tags'] = this.tags;

		if(this.specId.length > 0)
			pRes['spid'] = this.specId;

		// Dropdowns with remotely-loaded components are filled in automatically.
		if(this.type !== PARAM_TYPE.DROPDOWN_3D_MODEL 
			&& this.type !== PARAM_TYPE.DROPDOWN_MATERIAL 
			&& this.type !== PARAM_TYPE.DROPDOWN_PATH 
			&& this.type !== PARAM_TYPE.DROPDOWN_PROCEDURAL 
			&& this.type !== PARAM_TYPE.DROPDOWN_TEXTURE
			&& this.dropdownOptions.length > 0)
			pRes['ddo'] = this.dropdownOptions;

		return pRes;
	}

	loadFromObject(pObj, bSkipConfigDictFields = false)
	{
		super.loadFromObject(pObj);

		if(pObj['cni'] !== undefined)
			this.connectedNodeId = pObj['cni'];
		else
			this.connectedNodeId = -1;

		if(pObj['cno'])
			this.connectedNodeOutputName = pObj['cno'];
		else
			this.connectedNodeOutputName = null;

		if(pObj['e'])
			this.expression = pObj['e'];

		this.useExpression = !!pObj['usE'];

		if(!bSkipConfigDictFields && pObj['un'])
			this.units = pObj['un'];

		if(!bSkipConfigDictFields && pObj['mi'] !== undefined)
			this.minValue = pObj['mi'];

		if(!bSkipConfigDictFields && pObj['ma'] !== undefined)
			this.maxValue = pObj['ma'];

        if(pObj['tags'] !== undefined)
            this.tags = pObj['tags'];

        if(pObj['spid'] !== undefined)
            this.specId = pObj['spid'];

		if(!bSkipConfigDictFields && pObj['ddo'] !== undefined)
			this.dropdownOptions = pObj['ddo'];

		if(pObj['isInternal'])
			this.isInternal = true;

		this.loadStaticValueFrom(pObj['sv']);
	}

	getMinValue()
	{
		return this.minValue;
	}

	getMaxValue()
	{
		return this.maxValue;
	}

	setMinValue(fValue)
	{
		this.minValue = fValue;
	}

	setMaxValue(fValue)
	{
		this.maxValue = fValue;
	}

	getSpecId()
	{
		return this.specId;
	}

	setSpecId(specId)
	{
		this.specId = specId;
	}

    getTags()
	{
        return this.tags.filter(tag => tag && tag.length > 0);
    }

    setTags(tags)
	{
        this.tags = tags;
		this.tags.forEach((tag) => tag.trim());
		this.tags = this.tags.filter(tag => tag && tag.length > 0);
    }

	getIsWholeNodeInputHidden()
	{
		return this.isWholeNodeInputHidden;
	}

	setIsConnectorHidden(bValue)
	{
		this.isConnectorHidden = bValue;
	}

	getIsConnectorHidden()
	{
		return this.isConnectorHidden;
	}

	getIsInternal()
	{
		return this.isInternal;
	}

	setIsInternal(bValue)
	{
		this.isInternal = bValue;
	}

	getLastResult()
	{
		return this.lastComputedValue;
	}

	getConnectedNode()
	{
		return this.getOwnNode().getParent().getNode(this.connectedNodeId);
	}

	connectTo(iNodeId, strOutputName)
	{
		this.connectedNodeId = iNodeId;
		this.connectedNodeOutputName = strOutputName;

		let pSubnet = this.getOwnNode().getParent();
		let pConnectedNode = pSubnet.getNode(this.connectedNodeId);
		pConnectedNode.getOutput(strOutputName).finishConnectionTo(this.parentNode.getId(), this.name);

		this.onInputChanged();
	}
	
	getConnectedOutputName()
	{
		return this.connectedNodeOutputName;
	}

	renameConnectedOuputUnsafe(strNewName)
	{
		this.connectedNodeOutputName = strNewName;
	}
	
	computeCurrentValue(scene, pInputNames, currentNodeInputResults)
	{
		let pSourceNode = this.getConnectedNode();
		if(pSourceNode)
		{
			let pOutput = pSourceNode.getOutputConnectedTo(this.parentNode.getId(), this.name);
			this.lastComputedValue = pSourceNode.generate(scene, pOutput.getName());
		}
		else if(this.useExpression)
			this.lastComputedValue = Connector.evaluateExpression(this.getExpression(), pInputNames, currentNodeInputResults, this.name);
		else
		{
			// If nothing is connected to a geometry input, we return null
			// Important for switches, but also for proper types to be used.
			if(this.getType() === PARAM_TYPE.GEOMETRY)
				this.staticValue = null;
			else if(CompoundParameter.isCompoundParamType(this.getType()))
				this.lastComputedValue = this.staticValue.clone();
			else
				this.lastComputedValue = this.staticValue;
		}

		return this.lastComputedValue;
	}
	
	storeLastComputedValueInternal(value)
	{
		if(CompoundParameter.isCompoundParamType(this.getType()))
			this.lastComputedValue = value.clone();
		else
			this.lastComputedValue = value;
	}

	setStaticValue(value, optCompoundParameterName = null)
	{
		if(optCompoundParameterName)
		{
			// Set a parameter of our compound value
			this.lastComputedValue.setValue(optCompoundParameterName, value);

			if(this.staticValue.getValue(optCompoundParameterName) === value)
				return false;
			this.staticValue.setValue(optCompoundParameterName, value)
		}
		else
		{
			this.lastComputedValue = value;
			// TODO: How do we handle the case of general objects with numeric data? Possibly
			// not necessary right now, though.
			if(Utils.getIsSplineObject(value) && Utils.getIsSplineObject(this.staticValue))
			{
				// This is pretty inefficient potentially
				let spline1 = new Spline(this.staticValue);
				let spline2 = new Spline(value);
				if(spline1.getIsEqual(spline2))
					return false;
			}
			else if(this.staticValue === value)
				return false;
			this.staticValue = value;
		}

		this.onInputChanged();
		return true;
	}

	onInputChanged()
	{
		this.getOwnNode().onInputChanged(this.getName());
	}

	setType(eType)
	{
		const eOldType = this.getType();
		super.setType(eType);

		const eNewType = this.getType();
		if(Utils.isDropdownParam(eNewType) && eNewType !== PARAM_TYPE.DROPDOWN)
			this.getOwnNode().autoFillInputOptions([this]);
		else if(Utils.isDropdownParam(eOldType) && eOldType !== PARAM_TYPE.DROPDOWN)
			this.setDropdownOptions([]);
		else if(CompoundParameter.isCompoundParamType(eNewType))
			this.setStaticValue(CompoundParameter.create(eNewType));
	}

}